import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BehaviorFish from '@/assets/icons/behavior_fish.svg?component';
import { Spinner } from '@/components/atoms/Spinner';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { localizedDateFormat } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import { magnifiedImageSize } from '../../helpers/boxGeometry';
import { PLACEHOLDER_IMAGE_SRC } from '../../helpers/placeholderImage';
import { useStageZoom } from '../../hooks/useStageZoom';
import type { ImageBox, ImageSize } from '../../types';
import type { BoxTooltipStyle } from '../../variants';
import BoxOverlay from '../BoxOverlay/BoxOverlay';
import Magnifier from '../Magnifier/Magnifier';

import ZoomControls from './ZoomControls';

import styles from './ImageStage.module.scss';

export interface ImageStageProps {
    isColor?: boolean;
    id: string;
    path: string;
    text?: string;
    lazyLoad?: boolean;
    alt?: string;
    timeStamp: string;
    formattedTimeStamp?: string;
    dimensions: ImageSize;
    bboxes?: ImageBox[];
    /** Called with the box overlay's rect, which is what sizes the magnifier. */
    onOverlayMeasured?: (rect: DOMRect) => void;
    showPoiMeta?: boolean;
    showPoiTag?: boolean;
    magnifierOn?: boolean;
    boxTooltipStyle: BoxTooltipStyle;
    isOptoScaleAdmin?: boolean;
    /** Swiping to the next image stands down while the image is zoomed, so a drag can pan. */
    onZoomedChange?: (zoomed: boolean) => void;
}

/** One image, with its detection overlay, magnifier, loading state and Panzoom instance. */
export default function ImageStage(props: ImageStageProps) {
    const {
        isColor = true,
        path,
        text,
        lazyLoad = true,
        alt,
        timeStamp,
        formattedTimeStamp,
        dimensions,
        bboxes,
        onOverlayMeasured,
        showPoiMeta = false,
        showPoiTag = true,
        magnifierOn = false,
        boxTooltipStyle,
        isOptoScaleAdmin = false,
        onZoomedChange,
    } = props;

    const { t, i18n } = useTranslation();

    const imageRef = useRef<HTMLImageElement>(null);
    const imageStageRef = useRef<HTMLDivElement>(null);
    const figureRef = useRef<HTMLElement>(null);
    const [loadedPath, setLoadedPath] = useState<string | null>(null);
    const [errorPath, setErrorPath] = useState<string | null>(null);
    const imageReady = loadedPath === path;
    const imageFailed = errorPath === path;
    const showLoading = !imageReady && !imageFailed;

    const locale = parseLocale(i18n.language);

    const hasRenderableImage = Boolean(path && dimensions?.width && dimensions?.height);

    const loadlazy = lazyLoad ? 'lazy' : 'eager';

    // Use pre-formatted timestamp if available (normalized to location timezone),
    // otherwise fall back to browser-local formatting
    const displayTimeStamp =
        formattedTimeStamp ??
        (timeStamp ? localizedDateFormat({ dateString: timeStamp, locale }) : null);
    const captionLabel = text ?? t('organisms.imageStage.recorded');
    const captionText = displayTimeStamp
        ? `${captionLabel} ${displayTimeStamp}`
        : `${captionLabel} ${t('organisms.imageStage.timeUnknown')}`;

    /* The custom properties the stylesheets need from the image's own dimensions.
       BoxOverlay.module.scss derives the overlay's height from --image-aspect-ratio, so if
       this stops running the overlay collapses and every box disappears; Magnifier's CSS
       offsets the magnified image by a fraction of --salmon-*. This used to sit inside the
       Panzoom effect (now useStageZoom), sharing its early return and its dependency list,
       which made the boxes hostage to anything done to the Panzoom lifecycle. */
    useEffect(() => {
        const node = figureRef.current;

        if (!node || !hasRenderableImage) {
            return;
        }

        const aspect = dimensions.width / dimensions.height;
        node.style.setProperty('--image-aspect-ratio', `${aspect}/1`);

        // The magnified copy must be rendered at exactly this size for the offsets to
        // land; Magnifier reads the same helper for its inline width/height.
        const magnified = magnifiedImageSize(dimensions, isColor);
        node.style.setProperty('--salmon-width', `${magnified.width}px`);
        node.style.setProperty('--salmon-height', `${magnified.height}px`);
    }, [dimensions, isColor, hasRenderableImage]);

    const zoom = useStageZoom({
        figureRef,
        imageKey: hasRenderableImage ? path : null,
        disabled: magnifierOn,
        onZoomedChange,
    });

    useEffect(() => {
        setErrorPath(null);
        const img = imageRef.current;
        if (img?.complete && img.naturalWidth > 0) {
            setLoadedPath(path);
        }
    }, [path]);

    if (!hasRenderableImage) {
        return null;
    }

    return (
        <div className={styles.imageStage} ref={imageStageRef}>
            <figure
                ref={figureRef}
                aria-busy={showLoading}
                data-image-failed={imageFailed ? 'true' : undefined}>
                {showLoading && (
                    <div className={styles.imageLoading}>
                        <Spinner size="lg" />
                    </div>
                )}
                {imageFailed && (
                    <div
                        className={styles.imageFallback}
                        role="img"
                        aria-label={t('organisms.imageStage.failed')}>
                        <SvgIcon
                            icon={BehaviorFish}
                            size="inherit"
                            svgProps={{ 'aria-hidden': true }}
                        />
                    </div>
                )}
                <Magnifier
                    magnifierOn={magnifierOn}
                    path={path}
                    isColor={isColor}
                    dimensions={dimensions}
                />

                <BoxOverlay
                    isColor={isColor}
                    showPoiMeta={showPoiMeta}
                    showPoiTag={showPoiTag}
                    dimensions={dimensions}
                    bboxes={bboxes}
                    onLayerMeasured={onOverlayMeasured}
                    show={imageReady && Boolean(imageRef.current?.complete)}
                    tooltipStyle={boxTooltipStyle}
                    isOptoScaleAdmin={isOptoScaleAdmin}
                />

                <img
                    data-path={path}
                    data-loading={imageReady ? undefined : 'true'}
                    src={path}
                    data-placeholder={PLACEHOLDER_IMAGE_SRC}
                    alt={alt ?? t('organisms.imageStage.alt')}
                    loading={loadlazy}
                    fetchPriority={lazyLoad ? undefined : 'high'}
                    onLoad={() => {
                        setLoadedPath(path);
                        setErrorPath(null);
                    }}
                    onError={() => {
                        setErrorPath(path);
                        setLoadedPath(null);
                    }}
                    ref={imageRef}
                />
                <figcaption>{captionText}</figcaption>
            </figure>
            <ZoomControls zoom={zoom} disabled={magnifierOn} />
        </div>
    );
}
